/* eslint-disable no-nested-ternary */
import type { Miscellaneous, SystemCssProperties } from '@dhl-ui/theme'
import { type ChipProps } from './Chip'
import { isNotDefault } from './is-not-default'

export type StylesProps = {
  misc: Miscellaneous
  spacing: (...factors: number[]) => string
  isClickable: boolean
  sx?: Pick<SystemCssProperties, 'position' | 'top' | 'left' | 'right' | 'bottom'>
} & Pick<ChipProps, 'color' | 'disabled' | 'size' | 'isSquare' | 'variant'>

export const getStyles = ({
  misc,
  size,
  sx,
  spacing,
  isSquare,
  isClickable,
  variant,
  color,
  disabled,
}: StylesProps): SystemCssProperties => {
  const isFilled = variant === 'filled'
  const isOutlined = variant === 'outlined'

  const hasColor = isNotDefault(color)

  return {
    ...sx,
    display: 'inline-flex',
    alignItems: 'center',
    px: spacing(size === 'small' ? 0.25 : 0.5),
    py: spacing(size === 'small' ? 0 : isOutlined ? 0.375 : 0.5),
    borderRadius: isSquare ? spacing(0.5) : misc.borderRadius(3),
    cursor: isClickable ? 'pointer' : 'default',
    userSelect: 'none',
    maxWidth: '100%',
    minHeight: size === 'small' ? spacing(2.5) : 'auto',

    backgroundColor: isFilled ? (hasColor ? `${color}.main` : 'grey.300') : 'transparent',
    color: isFilled
      ? hasColor
        ? `${color}.contrastText`
        : 'alternative.contrast'
      : hasColor
        ? `${color}.main`
        : 'alternative.contrast',

    border: isOutlined ? misc.borderStyle(hasColor ? `${color}.main` : 'components.chip.border') : 'none',

    ...(disabled && {
      opacity: 0.4,
      pointerEvents: 'none',
    }),

    ...(isClickable && {
      ...misc.createHoverOrActiveState({
        backgroundColor: hasColor ? `${color}.${isFilled ? 'dark' : 'light'}` : 'components.chip.hover',
      }),

      ...misc.createFocusVisibleState({
        outline: misc.borderStyle('info.main', 3),
        outlineOffset: misc.borderSize(2),
      }),
    }),
  }
}
